import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import * as API from '../../../services/API';
import PopupDialog from '../../shared/PopupDialog';

import { IModelOption, ISubscription } from '../ManageInterModelSubscriptions';

// Caps the open list to ~6-7 rows, as the other IMDL dropdowns do.
const LIST_ITEM_HEIGHT = 36;

// Fits the xs popup; the full name shows on hover only when trimmed.
const TAG_NAME_MAX_LENGTH = 40;

const TrimmedTagName: FunctionComponent<{ name: string }> = ({ name }) => (
  <Tooltip
    title={name}
    arrow
    disableHoverListener={name.length <= TAG_NAME_MAX_LENGTH}
  >
    <span>
      {name.length > TAG_NAME_MAX_LENGTH
        ? `${name.slice(0, TAG_NAME_MAX_LENGTH)}…`
        : name}
    </span>
  </Tooltip>
);

interface ICopySubscriptionDialogProps {
  subscription: ISubscription | null;
  clientId?: number;
  modelInstanceId?: number;
  onClose: () => void;
  // Copy done or source gone: the parent closes this and reloads its list.
  onFinished: () => void;
}

interface INotice {
  severity: 'info' | 'warning' | 'error';
  text: string;
}

// Reads only the HTTPError status and body message.
const errorView = (error: unknown): { status?: number; message?: string } => {
  const view = error as {
    status?: number;
    body?: { message?: string };
  } | null;
  return { status: view?.status, message: view?.body?.message };
};

const CopySubscriptionDialog: FunctionComponent<
  ICopySubscriptionDialogProps
> = ({ subscription, clientId, modelInstanceId, onClose, onFinished }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [tags, setTags] = useState<IModelOption[]>([]);
  const [selectedTagId, setSelectedTagId] = useState<number | ''>('');
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState<INotice | null>(null);
  // Source deleted by someone else: Save is pointless, the list behind is stale.
  const [isSourceGone, setIsSourceGone] = useState(false);
  const latestLoadRef = useRef(0);

  const subscriptionId = subscription?.id;

  // Subscription tags of this instance with no live subscription yet.
  const loadTags = useCallback(async (): Promise<void> => {
    latestLoadRef.current += 1;
    const loadId = latestLoadRef.current;
    setSelectedTagId('');
    setTags([]);
    if (!subscriptionId || !clientId || !modelInstanceId) return;

    setIsLoadingTags(true);
    try {
      const response = await API.load(
        `/clients/${clientId}/scenario_link_tags/instance_subscription_tags?model_instance_id=${modelInstanceId}&exclude_in_use=true`
      );
      if (loadId === latestLoadRef.current) setTags(response?.items || []);
    } catch {
      if (loadId === latestLoadRef.current) {
        setNotice({
          severity: 'error',
          text: 'Failed to load subscription tags. Use Refresh to retry.',
        });
      }
    } finally {
      if (loadId === latestLoadRef.current) setIsLoadingTags(false);
    }
  }, [subscriptionId, clientId, modelInstanceId]);

  useEffect(() => {
    setNotice(null);
    setIsSourceGone(false);
    loadTags();
  }, [loadTags]);

  const handleRefresh = (): void => {
    setNotice(null);
    loadTags();
  };

  const handleClose = (): void => {
    if (isSourceGone) onFinished();
    else onClose();
  };

  const handleSave = async (): Promise<void> => {
    if (!subscriptionId || !selectedTagId) return;
    setIsSaving(true);
    setNotice(null);
    try {
      await API.create(`/imdl_subscriptions/${subscriptionId}/copy`, {
        scenario_link_tag_id: selectedTagId,
      });
      enqueueSnackbar('Subscription copied', { variant: 'success' });
      onFinished();
    } catch (error: unknown) {
      const { status, message } = errorView(error);
      if (status === 404) {
        setIsSourceGone(true);
        setNotice({
          severity: 'info',
          text: 'This subscription no longer exists. Close this dialog to see the updated list.',
        });
      } else if (status === 409) {
        // Tag taken meanwhile: show why and reload the tags.
        setNotice({
          severity: 'warning',
          text:
            message === 'SUBSCRIPTION_EXISTS' || !message
              ? 'A subscription was just created for this tag. The tag list has been refreshed.'
              : `${message} The tag list has been refreshed.`,
        });
        loadTags();
      } else {
        setNotice({
          severity: status === 423 ? 'warning' : 'error',
          text: message || 'Failed to copy subscription',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const selectedTag = tags.find((tag) => tag.id === selectedTagId) || null;

  return (
    <PopupDialog
      open={!!subscription}
      title="Copy Subscription"
      close={handleClose}
      maxWidth="xs"
      isLoading={isSaving}
      primaryButtons={[
        {
          id: 'copy-subscription-save',
          label: isSaving ? 'Saving...' : 'Save',
          onClick: handleSave,
          disabled: isSaving || !selectedTagId || isSourceGone,
          loading: false,
        },
      ]}
    >
      <Box>
        {notice && (
          <Alert severity={notice.severity} sx={{ mb: 2 }}>
            {notice.text}
          </Alert>
        )}
        <Typography variant="caption" color="text.secondary">
          Copying from
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
          <TrimmedTagName name={subscription?.scenarioTag || ''} />
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Search plus capped list, like the other IMDL dropdowns. */}
          <Autocomplete
            fullWidth
            size="small"
            disabled={isSourceGone}
            loading={isLoadingTags}
            options={tags}
            getOptionLabel={(tag) => tag.Name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedTag}
            onChange={(e, newValue) => setSelectedTagId(newValue?.id ?? '')}
            loadingText="Loading subscription tags..."
            noOptionsText={
              tags.length === 0
                ? 'No subscription tags available for copying'
                : 'No results found'
            }
            ListboxProps={{
              style: { maxHeight: LIST_ITEM_HEIGHT * 6.5, overflowY: 'auto' },
            }}
            renderOption={(props, option) => (
              <Typography component="li" {...props} noWrap>
                <TrimmedTagName name={option.Name} />
              </Typography>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Subscription Scenario Tag"
                inputProps={{
                  ...params.inputProps,
                  // Selected name is clipped; full name on hover only if long.
                  title:
                    selectedTag && selectedTag.Name.length > TAG_NAME_MAX_LENGTH
                      ? selectedTag.Name
                      : '',
                  style: { textOverflow: 'ellipsis' },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingTags && (
                        <CircularProgress color="inherit" size={18} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Tooltip title="Refresh tags">
            <span>
              <IconButton
                aria-label="Refresh tags"
                onClick={handleRefresh}
                disabled={isLoadingTags || isSaving || isSourceGone}
              >
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </PopupDialog>
  );
};

export default CopySubscriptionDialog;
